import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import API_BASE_URL from '../config/apiConfig';
import countryOptions from '../constants/countryOptions';

const ADDRESS_LIMIT = 5;

const emptyAddressForm = (user, addressType = 'billing') => ({
    AddressId: null,
    AddressType: addressType,
    FirstName: user?.UserName || '',
    LastName: '',
    Phone: user?.Phone || '',
    AddressLine1: '',
    AddressLine2: '',
    City: '',
    State: '',
    Country: '',
    Zipcode: '',
    IsDefault: false,
});

const addressTypeRank = (addressType) => (String(addressType || '').toLowerCase() === 'billing' ? 0 : 1);

const sortAddresses = (list) => [...list].sort((left, right) => {
    const leftDefault = Boolean(left.IsDefault);
    const rightDefault = Boolean(right.IsDefault);
    if (leftDefault !== rightDefault) {
        return leftDefault ? -1 : 1;
    }

    const typeDiff = addressTypeRank(left.AddressType) - addressTypeRank(right.AddressType);
    if (typeDiff !== 0) {
        return typeDiff;
    }

    const leftName = `${left.FirstName || ''} ${left.LastName || ''}`.trim().toLowerCase();
    const rightName = `${right.FirstName || ''} ${right.LastName || ''}`.trim().toLowerCase();
    return leftName.localeCompare(rightName);
});

const formatTypeLabel = (addressType) => {
    const normalized = String(addressType || '').toLowerCase();
    return normalized === 'shipping' ? 'Shipping' : 'Billing';
};

function AddressCard({
    address,
    shippingAtLimit,
    onEdit,
    onDelete,
    onSetDefault,
    onCopyAsShipping,
}) {
    const cityLine = [address.City, address.State].filter(Boolean).join(', ');
    const countryLine = [address.Country, address.Zipcode].filter(Boolean).join(' · ');
    const street = [address.AddressLine1, address.AddressLine2].filter(Boolean).join(', ');
    const typeLabel = formatTypeLabel(address.AddressType);
    const isBilling = String(address.AddressType || '').toLowerCase() === 'billing';

    return (
        <div className="col-12 col-md-6 col-xl-4">
            <article className="card h-100 address-book-card">
                <div className="card-body d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-start gap-2 mb-10">
                        <div className="d-flex flex-wrap gap-2">
                            <span className="badge bg-light text-dark border">{typeLabel}</span>
                            {address.IsDefault ? <span className="badge bg-success">Default</span> : null}
                        </div>
                    </div>
                    <strong className="d-block mb-5">
                        {address.FirstName} {address.LastName || ''}
                    </strong>
                    <address className="mb-10 address-book-clamp">
                        {street}
                        {cityLine ? <><br />{cityLine}</> : null}
                        {countryLine ? <><br />{countryLine}</> : null}
                    </address>
                    <p className="mb-15 text-muted">
                        <i className="fi-rs-phone mr-5" aria-hidden="true"></i>
                        {address.Phone || '—'}
                    </p>
                    <div className="d-flex flex-wrap gap-2 mt-auto">
                        <button type="button" className="btn-small border-0" onClick={() => onEdit(address)}>
                            Edit
                        </button>
                        {!address.IsDefault ? (
                            <button type="button" className="btn-small border-0" onClick={() => onSetDefault(address)}>
                                Set default
                            </button>
                        ) : null}
                        <button type="button" className="btn-small border-0" onClick={() => onDelete(address.AddressId)}>
                            Delete
                        </button>
                        {isBilling && !shippingAtLimit ? (
                            <button type="button" className="btn-small border-0" onClick={() => onCopyAsShipping(address)}>
                                Save as shipping
                            </button>
                        ) : null}
                    </div>
                </div>
            </article>
        </div>
    );
}

function AddressBookSection({ user, token }) {
    const deleteModalRef = useRef(null);
    const deleteConfirmRef = useRef(null);
    const firstNameRef = useRef(null);
    const typeSelectRef = useRef(null);
    const userRef = useRef(user);
    const filterRef = useRef('all');
    const editingAddressIdRef = useRef(null);
    userRef.current = user;

    const [addresses, setAddresses] = useState([]);
    const [isAddressesLoading, setIsAddressesLoading] = useState(false);
    const [addressesError, setAddressesError] = useState('');
    const [addressActionMessage, setAddressActionMessage] = useState('');
    const [isAddressSaving, setIsAddressSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [editingAddressId, setEditingAddressId] = useState(null);
    const [addressForm, setAddressForm] = useState(() => emptyAddressForm(user));
    const [filter, setFilter] = useState('all');
    const [isOffcanvasOpen, setIsOffcanvasOpen] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState(null);
    filterRef.current = filter;
    editingAddressIdRef.current = editingAddressId;

    const refreshAddresses = useCallback(async () => {
        if (!user?.UserId || !token) {
            setAddresses([]);
            return;
        }

        const response = await fetch(`${API_BASE_URL}/ec/address/getByUser`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        const result = await response.json();
        if (!response.ok || !result?.success) {
            throw new Error(result?.message || 'Unable to fetch addresses.');
        }

        setAddresses(Array.isArray(result.data) ? result.data : []);
    }, [token, user?.UserId]);

    useEffect(() => {
        const fetchAddresses = async () => {
            if (!user?.UserId || !token) {
                setAddresses([]);
                return;
            }

            setIsAddressesLoading(true);
            setAddressesError('');

            try {
                await refreshAddresses();
            } catch (error) {
                setAddressesError(error?.message || 'Unable to reach server. Please try again.');
                setAddresses([]);
            } finally {
                setIsAddressesLoading(false);
            }
        };

        fetchAddresses();
    }, [refreshAddresses, token, user?.UserId]);

    useEffect(() => {
        if (!isOffcanvasOpen) {
            return undefined;
        }

        const focusTarget = editingAddressIdRef.current ? firstNameRef.current : typeSelectRef.current;
        focusTarget?.focus();

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setIsOffcanvasOpen(false);
                setEditingAddressId(null);
                const preset = filterRef.current === 'shipping' ? 'shipping' : 'billing';
                setAddressForm(emptyAddressForm(userRef.current, preset));
            }
        };

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOffcanvasOpen]);

    useEffect(() => () => {
        const modalElement = deleteModalRef.current;
        window.bootstrap?.Modal?.getInstance(modalElement)?.hide();
    }, []);

    useEffect(() => {
        const element = deleteModalRef.current;
        const Modal = window.bootstrap?.Modal;
        if (!element || !Modal || !pendingDeleteId) {
            return undefined;
        }

        const instance = Modal.getInstance(element) || new Modal(element);
        const handleHidden = () => setPendingDeleteId(null);
        const handleShown = () => deleteConfirmRef.current?.focus();

        element.addEventListener('hidden.bs.modal', handleHidden);
        element.addEventListener('shown.bs.modal', handleShown);
        instance.show();

        return () => {
            element.removeEventListener('hidden.bs.modal', handleHidden);
            element.removeEventListener('shown.bs.modal', handleShown);
        };
    }, [pendingDeleteId]);

    const billingAddresses = useMemo(
        () => addresses.filter((address) => String(address.AddressType || '').toLowerCase() === 'billing'),
        [addresses]
    );
    const shippingAddresses = useMemo(
        () => addresses.filter((address) => String(address.AddressType || '').toLowerCase() === 'shipping'),
        [addresses]
    );

    const visibleAddresses = useMemo(() => {
        if (filter === 'billing') {
            return sortAddresses(billingAddresses);
        }
        if (filter === 'shipping') {
            return sortAddresses(shippingAddresses);
        }
        return sortAddresses(addresses);
    }, [addresses, billingAddresses, filter, shippingAddresses]);

    const addDisabled = (
        (filter === 'billing' && billingAddresses.length >= ADDRESS_LIMIT)
        || (filter === 'shipping' && shippingAddresses.length >= ADDRESS_LIMIT)
        || (filter === 'all' && billingAddresses.length >= ADDRESS_LIMIT && shippingAddresses.length >= ADDRESS_LIMIT)
    );

    const presetTypeForAdd = () => {
        if (filter === 'shipping') {
            return 'shipping';
        }
        if (filter === 'billing') {
            return 'billing';
        }
        if (billingAddresses.length >= ADDRESS_LIMIT && shippingAddresses.length < ADDRESS_LIMIT) {
            return 'shipping';
        }
        return 'billing';
    };

    const openAddOffcanvas = () => {
        if (addDisabled) {
            return;
        }
        setAddressesError('');
        setAddressActionMessage('');
        setEditingAddressId(null);
        setAddressForm(emptyAddressForm(user, presetTypeForAdd()));
        setIsOffcanvasOpen(true);
    };

    const openEditOffcanvas = (address) => {
        setAddressesError('');
        setAddressActionMessage('');
        setEditingAddressId(address.AddressId);
        setAddressForm({
            AddressId: address.AddressId,
            AddressType: String(address.AddressType || 'billing').toLowerCase(),
            FirstName: address.FirstName || '',
            LastName: address.LastName || '',
            Phone: address.Phone || '',
            AddressLine1: address.AddressLine1 || '',
            AddressLine2: address.AddressLine2 || '',
            City: address.City || '',
            State: address.State || '',
            Country: address.Country || '',
            Zipcode: address.Zipcode || '',
            IsDefault: Boolean(address.IsDefault),
        });
        setIsOffcanvasOpen(true);
    };

    const openCopyAsShipping = (address) => {
        if (shippingAddresses.length >= ADDRESS_LIMIT) {
            return;
        }
        setAddressesError('');
        setAddressActionMessage('');
        setEditingAddressId(null);
        setAddressForm({
            AddressId: null,
            AddressType: 'shipping',
            FirstName: address.FirstName || '',
            LastName: address.LastName || '',
            Phone: address.Phone || '',
            AddressLine1: address.AddressLine1 || '',
            AddressLine2: address.AddressLine2 || '',
            City: address.City || '',
            State: address.State || '',
            Country: address.Country || '',
            Zipcode: address.Zipcode || '',
            IsDefault: false,
        });
        setIsOffcanvasOpen(true);
    };

    const handleAddressInputChange = (event) => {
        const { name, value, type, checked } = event.target;
        setAddressForm((previous) => ({
            ...previous,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const closeOffcanvas = () => {
        setIsOffcanvasOpen(false);
        setEditingAddressId(null);
        const preset = filterRef.current === 'shipping' ? 'shipping' : 'billing';
        setAddressForm(emptyAddressForm(userRef.current, preset));
    };

    const handleSaveAddress = async (event) => {
        event.preventDefault();

        setIsAddressSaving(true);
        setAddressActionMessage('');
        setAddressesError('');

        try {
            const response = await fetch(`${API_BASE_URL}/ec/address/save`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(addressForm),
            });

            const result = await response.json();
            if (!response.ok || !result?.success) {
                setAddressesError(result?.message || 'Unable to save address.');
                return;
            }

            setAddressActionMessage(result?.message || 'Address saved successfully.');
            await refreshAddresses();
            closeOffcanvas();
        } catch (_) {
            setAddressesError('Unable to reach server. Please try again.');
        } finally {
            setIsAddressSaving(false);
        }
    };

    const handleDeleteAddress = async (addressId) => {
        setIsDeleting(true);
        setAddressActionMessage('');
        setAddressesError('');

        try {
            const response = await fetch(`${API_BASE_URL}/ec/address/delete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ AddressId: addressId }),
            });

            const result = await response.json();
            if (!response.ok || !result?.success) {
                setAddressesError(result?.message || 'Unable to delete address.');
                return;
            }

            setAddressActionMessage('Address deleted successfully.');
            await refreshAddresses();
            if (editingAddressId === addressId) {
                closeOffcanvas();
            }
        } catch (_) {
            setAddressesError('Unable to reach server. Please try again.');
        } finally {
            setIsDeleting(false);
            const element = deleteModalRef.current;
            const instance = element && window.bootstrap?.Modal?.getInstance(element);
            instance?.hide();
        }
    };

    const handleSetDefaultAddress = async (address) => {
        setAddressActionMessage('');
        setAddressesError('');

        try {
            const response = await fetch(`${API_BASE_URL}/ec/address/save`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    ...address,
                    IsDefault: true,
                }),
            });

            const result = await response.json();
            if (!response.ok || !result?.success) {
                setAddressesError(result?.message || 'Unable to set default address.');
                return;
            }

            setAddressActionMessage('Default address updated successfully.');
            await refreshAddresses();
        } catch (_) {
            setAddressesError('Unable to reach server. Please try again.');
        }
    };

    const emptyCopy = filter === 'billing'
        ? 'No billing addresses yet.'
        : filter === 'shipping'
            ? 'No shipping addresses yet.'
            : 'No addresses saved.';

    const limitHelper = filter === 'billing' && billingAddresses.length >= ADDRESS_LIMIT
        ? 'Maximum 5 billing addresses allowed.'
        : filter === 'shipping' && shippingAddresses.length >= ADDRESS_LIMIT
            ? 'Maximum 5 shipping addresses allowed.'
            : filter === 'all' && billingAddresses.length >= ADDRESS_LIMIT && shippingAddresses.length >= ADDRESS_LIMIT
                ? 'Maximum 5 addresses allowed for billing and shipping.'
                : '';

    const countrySelectValue = countryOptions.some((option) => option.label === addressForm.Country)
        ? addressForm.Country
        : addressForm.Country || '';

    const filterButtonClass = (value) => (
        filter === value
            ? 'btn btn-sm btn-fill-out'
            : 'btn btn-sm btn-outline-secondary'
    );

    return (
        <div className="card">
            <div className="card-header">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <h3 className="mb-0">My addresses</h3>
                    <button
                        type="button"
                        className="btn btn-fill-out submit font-weight-bold"
                        onClick={openAddOffcanvas}
                        disabled={addDisabled}
                    >
                        Add address
                    </button>
                </div>
            </div>
            <div className="card-body">
                <div className="d-flex flex-wrap gap-2 align-items-center mb-20">
                    <button type="button" className={filterButtonClass('all')} onClick={() => setFilter('all')}>
                        All ({addresses.length})
                    </button>
                    <button type="button" className={filterButtonClass('billing')} onClick={() => setFilter('billing')}>
                        Billing ({billingAddresses.length}/{ADDRESS_LIMIT})
                    </button>
                    <button type="button" className={filterButtonClass('shipping')} onClick={() => setFilter('shipping')}>
                        Shipping ({shippingAddresses.length}/{ADDRESS_LIMIT})
                    </button>
                </div>

                {addressesError ? <p className="text-danger mb-20">{addressesError}</p> : null}
                {addressActionMessage ? <p className="text-success mb-20">{addressActionMessage}</p> : null}
                {limitHelper ? <p className="mb-20">{limitHelper}</p> : null}

                {isAddressesLoading ? (
                    <p className="text-center mb-0">
                        <i className="fi-rs-spinner mr-5" aria-hidden="true"></i>
                        Loading addresses...
                    </p>
                ) : null}

                {!isAddressesLoading && !visibleAddresses.length ? (
                    <div className="text-center py-4">
                        <i className="fi-rs-marker d-block mb-10" aria-hidden="true"></i>
                        <p className="mb-15">{emptyCopy}</p>
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={openAddOffcanvas}
                            disabled={addDisabled}
                        >
                            Add your first address
                        </button>
                    </div>
                ) : null}

                {!isAddressesLoading && visibleAddresses.length ? (
                    <div className="row g-3">
                        {visibleAddresses.map((address) => (
                            <AddressCard
                                key={address.AddressId}
                                address={address}
                                shippingAtLimit={shippingAddresses.length >= ADDRESS_LIMIT}
                                onEdit={openEditOffcanvas}
                                onDelete={setPendingDeleteId}
                                onSetDefault={handleSetDefaultAddress}
                                onCopyAsShipping={openCopyAsShipping}
                            />
                        ))}
                    </div>
                ) : null}
            </div>

            {isOffcanvasOpen ? createPortal(
            <div className="address-offcanvas-root">
            <button type="button" className="address-offcanvas-backdrop" aria-label="Close address form" onClick={closeOffcanvas}></button>
            <aside
                className="address-offcanvas"
                role="dialog"
                aria-modal="true"
                aria-labelledby="addressBookOffcanvasLabel"
            >
                <div className="offcanvas-header">
                    <div>
                        <h5 className="mb-0" id="addressBookOffcanvasLabel">
                            {editingAddressId ? 'Edit address' : 'Add address'}
                        </h5>
                        <p className="mb-0 text-muted text-capitalize">{addressForm.AddressType}</p>
                    </div>
                    <button type="button" className="btn-close" aria-label="Close" onClick={closeOffcanvas}></button>
                </div>
                <form className="address-offcanvas-form" onSubmit={handleSaveAddress}>
                    <div className="offcanvas-body">
                        <div className="form-group mb-20">
                            <label htmlFor="address-type">Address type</label>
                            <select
                                id="address-type"
                                ref={typeSelectRef}
                                className="form-control"
                                name="AddressType"
                                value={addressForm.AddressType}
                                onChange={handleAddressInputChange}
                                disabled={Boolean(editingAddressId)}
                            >
                                <option value="billing">Billing</option>
                                <option value="shipping">Shipping</option>
                            </select>
                        </div>
                        <div className="form-group mb-20">
                            <label htmlFor="address-first-name">First name</label>
                            <input
                                id="address-first-name"
                                ref={firstNameRef}
                                className="form-control"
                                name="FirstName"
                                value={addressForm.FirstName}
                                onChange={handleAddressInputChange}
                                required
                            />
                        </div>
                        <div className="form-group mb-20">
                            <label htmlFor="address-last-name">Last name</label>
                            <input
                                id="address-last-name"
                                className="form-control"
                                name="LastName"
                                value={addressForm.LastName}
                                onChange={handleAddressInputChange}
                            />
                        </div>
                        <div className="form-group mb-20">
                            <label htmlFor="address-phone">Phone</label>
                            <input
                                id="address-phone"
                                className="form-control"
                                name="Phone"
                                type="tel"
                                value={addressForm.Phone}
                                onChange={handleAddressInputChange}
                            />
                        </div>
                        <div className="form-group mb-20">
                            <label htmlFor="address-line-1">Address line 1</label>
                            <input
                                id="address-line-1"
                                className="form-control"
                                name="AddressLine1"
                                value={addressForm.AddressLine1}
                                onChange={handleAddressInputChange}
                                required
                            />
                        </div>
                        <div className="form-group mb-20">
                            <label htmlFor="address-line-2">Address line 2</label>
                            <input
                                id="address-line-2"
                                className="form-control"
                                name="AddressLine2"
                                value={addressForm.AddressLine2}
                                onChange={handleAddressInputChange}
                            />
                        </div>
                        <div className="form-group mb-20">
                            <label htmlFor="address-city">City</label>
                            <input
                                id="address-city"
                                className="form-control"
                                name="City"
                                value={addressForm.City}
                                onChange={handleAddressInputChange}
                                required
                            />
                        </div>
                        <div className="form-group mb-20">
                            <label htmlFor="address-state">State</label>
                            <input
                                id="address-state"
                                className="form-control"
                                name="State"
                                value={addressForm.State}
                                onChange={handleAddressInputChange}
                            />
                        </div>
                        <div className="form-group mb-20">
                            <label htmlFor="address-country">Country</label>
                            <select
                                id="address-country"
                                className="form-control"
                                name="Country"
                                value={countrySelectValue}
                                onChange={handleAddressInputChange}
                            >
                                <option value="">Select country</option>
                                {addressForm.Country && !countryOptions.some((option) => option.label === addressForm.Country) ? (
                                    <option value={addressForm.Country}>{addressForm.Country}</option>
                                ) : null}
                                {countryOptions.map((option) => (
                                    <option key={option.value} value={option.label}>{option.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group mb-20">
                            <label htmlFor="address-zipcode">Zipcode</label>
                            <input
                                id="address-zipcode"
                                className="form-control"
                                name="Zipcode"
                                value={addressForm.Zipcode}
                                onChange={handleAddressInputChange}
                                required
                            />
                        </div>
                        <div className="address-default-toggle mb-0">
                            <input
                                id="address-is-default"
                                type="checkbox"
                                className="address-default-toggle-input"
                                name="IsDefault"
                                checked={addressForm.IsDefault}
                                onChange={handleAddressInputChange}
                            />
                            <label className="address-default-toggle-label" htmlFor="address-is-default">
                                Set as default {addressForm.AddressType}
                            </label>
                        </div>
                    </div>
                    <div className="address-offcanvas-footer">
                        <button type="submit" className="btn btn-fill-out submit font-weight-bold" disabled={isAddressSaving}>
                            {isAddressSaving ? 'Saving...' : 'Save address'}
                        </button>
                        <button type="button" className="btn btn-outline-secondary" onClick={closeOffcanvas}>
                            Cancel
                        </button>
                    </div>
                </form>
            </aside>
            </div>,
            document.body
            ) : null}

            {createPortal(
            <div
                className="modal fade"
                id="addressDeleteModal"
                tabIndex="-1"
                ref={deleteModalRef}
                aria-labelledby="addressDeleteModalLabel"
            >
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="addressDeleteModalLabel">Delete address</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            Delete this address? This cannot be undone.
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                ref={deleteConfirmRef}
                                disabled={isDeleting || !pendingDeleteId}
                                onClick={() => handleDeleteAddress(pendingDeleteId)}
                            >
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>,
            document.body
            )}
        </div>
    );
}

export default AddressBookSection;
